import type { ParsedRule } from '@/pages/Screener/functions';
import { gql } from 'graphql-request';
import { request, requestWithAuth } from '@/utilities/graphQLClient';
import type { QueryFunctionContext } from '@tanstack/react-query';
import type { RawGqlCompany } from './types';
export type QueryKeyParams = {
  rules?: ParsedRule[];
  limit: number;
  initPage: number;
  isGuest: boolean;
  returnRecentCompaniesOnly?: boolean;
};

export type QueryKeyResult = [
  string,
  ParsedRule[] | undefined,
  number,
  number,
  boolean,
  boolean,
];

export const getQueryKey = ({
  rules,
  limit,
  initPage,
  isGuest,
  returnRecentCompaniesOnly,
}: QueryKeyParams): QueryKeyResult => {
  return [
    'company-screeners',
    rules,
    limit,
    initPage,
    isGuest,
    Boolean(returnRecentCompaniesOnly),
  ] as const;
};

type RestrictionsCode =
  | 'PLAN_LIMIT_COMPANY_COUNT'
  | 'USER_IS_GUEST'
  | 'PLAN_LIMIT_NEW_COMPANY'
  | 'RESULT_SET_TOO_LARGE_FOR_IS_NEW'
  | 'PIT_SEARCH_OFF'
  | 'RESULT_SET_TOO_LARGE_FOR_ONLY_NEW';

export type Restrictions = {
  isRestricted: boolean;
  restrictionCode: RestrictionsCode | null;
  restrictionReason: string | null;
};

export type CompanyScreenerResults = {
  totalHits: number | null;
  newCompaniesTotalHits: number | null;
  companies: RawGqlCompany[] | null;
  meta: {
    searchRestrictions: Restrictions | null;
    systemRestrictions: Restrictions | null;
  } | null;
};

export type QueryResponse = { companyScreenerResults?: CompanyScreenerResults };

const screenerQuery = gql`
  query FindStocks(
    $filterJson: String!
    $limit: Int!
    $offset: Int!
    $returnRecentCompaniesOnly: Boolean
  ) {
    companyScreenerResults(
      input: {
        limit: $limit
        offset: $offset
        filterJson: $filterJson
        returnRecentCompaniesOnly: $returnRecentCompaniesOnly
      }
    ) {
      totalHits
      newCompaniesTotalHits
      meta {
        searchRestrictions {
          isRestricted
          restrictionCode
          restrictionReason
        }
        systemRestrictions {
          isRestricted
          restrictionCode
          restrictionReason
        }
      }
      companies {
        id
        name
        nameSlug
        canonicalURL
        exchangeSymbol
        listingCurrencyISO
        isRecentSearchEntry
        primaryIndustry {
          id
          slug
          name
        }
        score {
          dividend
          future
          health
          past
          value
        }
        tickerSymbol
        uniqueSymbol
        analysisValue {
          return1d
          return7d
          return1yAbs
          marketCap
          lastSharePrice
          priceTarget
          pe
          pb
          priceToSales
        }
        analysisMisc {
          analystCount
        }
        analysisDividend {
          dividendYield
        }
        analysisFuture {
          netIncomeGrowth3Y
          netIncomeGrowthAnnual
          revenueGrowthAnnual
        }

        info {
          url
          shortDescription
          coverSmallUrl
          yearFounded
          logoUrl
          legalName
        }
      }
    }
  }
`;

export const queryFn = async ({
  queryKey: [_key, rules, limit, initPage, isGuest, returnRecentCompaniesOnly],
  pageParam = initPage,
}: QueryFunctionContext<QueryKeyResult, number>) => {
  const isValid = rules && rules.length && limit > 0 && pageParam >= 0;
  if (!isValid) {
    throw new Error('Params are invalid');
  }

  const filterJson = JSON.stringify(rules);
  const offset = pageParam * limit;
  const requestFunc = isGuest ? request : requestWithAuth;
  const response = await requestFunc<QueryResponse>(screenerQuery, {
    filterJson,
    limit,
    offset,
    returnRecentCompaniesOnly,
  });

  if (response.companyScreenerResults === undefined) {
    throw new Error('Cannot query company screeners');
  }

  return response.companyScreenerResults;
};
